import { memo, useMemo, useContext, type MouseEventHandler, type ReactNode, type FunctionComponent } from 'react';
import map from 'lodash/map';
import join from 'lodash/join';
import trim from 'lodash/trim';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import toString from 'lodash/toString';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// Skillmore UI Components
import { bold, mapChunks } from '@empathco/ui-components/src/helpers/intl';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
// local imports
import useCustomerSettings from '../config/customer';
import useModels from '../helpers/models';
import { GlobalContext } from '../context/global';
// Media and SCSS imports
import { title, intro, button, buttonPending, privacyLink, textLink } from './SplashText.module.scss';

export type SplashVariant = 'welcome' | 'access_denied' | 'logging_in' | 'login' | 'multiple_sso' | 'sso' | 'set_password';

type SplashTextProps = {
  readonly children?: ReactNode;
  readonly variant: SplashVariant;
  readonly name?: string;
  readonly privacy?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly pending?: boolean | null;
  readonly disabled?: boolean | null;
};

const SplashText: FunctionComponent<SplashTextProps> = ({
  children,
  variant,
  name,
  privacy = false,
  onClick,
  pending = false,
  disabled = false
}) => {
  const { formatMessage } = useIntl();
  const { PRIVACY_NOTICE_URL, PROFILE_BUILDER_VIDEO_URL, PROFILE_BUILDER_MANUAL_URL } = useCustomerSettings();
  const { getUserTags } = useModels();
  const { user: { data: user } } = useContext(GlobalContext);

  const values = useMemo(() => ({
    ...getUserTags(user),
    br: <br/>,
    bold,
    name,
    company: <FormattedMessage id="header.company"/>,
    video: Boolean(variant === 'welcome' && PROFILE_BUILDER_VIDEO_URL),
    guide: Boolean(variant === 'welcome' && (PROFILE_BUILDER_VIDEO_URL || PROFILE_BUILDER_MANUAL_URL)),
    // eslint-disable-next-line react/no-unstable-nested-components
    link: (chunks?: ReactNode | null): ReactNode => PROFILE_BUILDER_VIDEO_URL || PROFILE_BUILDER_MANUAL_URL ? (
      <StandardLink href={PROFILE_BUILDER_VIDEO_URL || PROFILE_BUILDER_MANUAL_URL} className={textLink}>
        {mapChunks(chunks)}
      </StandardLink>
    ) : (
      <>
        {mapChunks(chunks)}
      </>
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    mailto: (chunks?: ReactNode | null): ReactNode => (
      <StandardLink href={`mailto:${isArray(chunks) ? join(map(chunks, toString)) : toString(chunks)}`} className={textLink}>
        {mapChunks(chunks)}
      </StandardLink>
    )
  }), [name, user, variant, PROFILE_BUILDER_VIDEO_URL, PROFILE_BUILDER_MANUAL_URL, getUserTags]);

  const welcomeNote = useMemo(() => {
    if (variant !== 'welcome') return undefined;
    const msg = formatMessage({ id: `splash.${variant}.welcome_note` }, values);
    return isString(msg) ? trim(msg) : msg;
  }, [values, variant, formatMessage]);

  return (
    <>
      <BoxTypography pt={{ xs: 5, sm: 14 }} px={{ xs: 2.5, sm: 6 }} variant="h2" align="center" className={title}>
        <FormattedMessage
            id={`splash.${variant}.hello`}
            values={values}
        />
      </BoxTypography>
      <BoxTypography pt={{ xs: 3, sm: 9 }} px={{ xs: 2.5, sm: 6 }} variant="subtitle1" align="center" className={intro}>
        <FormattedMessage
            id={`splash.${variant}.intro`}
            values={values}
        />
      </BoxTypography>
      {welcomeNote ? (
        <BoxTypography pt={{ xs: 3, sm: 6 }} px={{ xs: 2.5, sm: 6 }} variant="subtitle2" align="center" className={intro}>
          {welcomeNote}
        </BoxTypography>
      ) : undefined}
      {children}
      {onClick ? (
        <Box
            sx={{
              pt: { xs: 6, sm: welcomeNote ? 8 : 11 },
              pb: { xs: 6, sm: welcomeNote ? 12 : 22 },
              px: { xs: 2.5, sm: 6 },
              display: 'flex',
              justifyContent: 'center'
            }}
        >
          <Button
              color={pending || disabled ? 'info' : 'primary'}
              variant="contained"
              size="large"
              disableElevation
              onClick={onClick}
              disabled={pending || disabled ? true : undefined}
              startIcon={pending ? <CircularProgress size={18} color="inherit"/> : undefined}
              className={pending ? buttonPending : button}
          >
            <FormattedMessage id={`splash.${variant}.button`}/>
          </Button>
        </Box>
      ) : undefined}
      {PRIVACY_NOTICE_URL && privacy ? (
        <Box sx={{ pb: { xs: 2.5, sm: 6 }, px: { xs: 2.5, sm: 6 }, textAlign: 'center', zIndex: 100 }}>
          <StandardLink
              variant="subtitle1"
              active
              text="welcome.data_privacy.text"
              href={PRIVACY_NOTICE_URL}
              className={privacyLink}
          />
        </Box>
      ) : undefined}
    </>
  );
};

export default memo(SplashText);
